/**
 * 확인 필요 의약품 모달.
 * 레거시 ConfirmNeededDrugsModal 화면 구성(정산 테이블 + 3탭 후보 검색)을 RDS로 재구현한다.
 */
import {
  drugDataService,
  type CommissionValidationItem,
  type DrugBasicInfoCandidate,
  type DrugCandidateSearchResult,
  type DrugCodeMappingCandidate,
  type DrugManualDetail,
  type DrugMfdsCandidate,
} from '@/entities/drug-data/api/drug-data.api';
import { useUpdateDrugDataDrug } from '@/entities/drug-data/hooks/useDrugData';
import { CreatedDrugDetailModal } from '@/pages/drugs/CreatedDrugDetailModal';
import {
  buildSettlementDrugSearchKeyword,
  candidateListCount,
  CONFIRM_NEEDED_TAB_KEYS,
  countDrugCandidateResults,
  displayCell,
  formatDelimitedMultiline,
  isSettlementRowCreated,
  resolvePharmaCompanyName,
  resolveValidationPharmaDrugCode,
  resolveValidationPrice,
  validationRowKey,
  type ConfirmNeededTabKey,
} from '@/pages/drugs/confirmNeededDrugsModalUtils';
import { buildBasicCopyPatchRequest } from '@/pages/drugs/drugCodeCopyPayload';
import {
  validationItemToSettlementRow,
  resolveEditDrugCodeFromDetail,
  type SettlementRow,
} from '@/pages/drugs/drugCreateFormUtils';
import { DrugInfoCreateFromSettlementModal } from '@/pages/drugs/DrugInfoCreateFromSettlementModal';
import {
  REFERENCE_TAB_KEYS,
  type ReferenceTabKey,
} from '@/pages/drugs/drugFormOptions';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/radio-group';
import { FooterRow, Row, ToolbarRow } from '@/shared/components/ui/row';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { ExternalLink, Search } from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const TAB_REFERENCE_KEY: Record<ConfirmNeededTabKey, ReferenceTabKey> = {
  [CONFIRM_NEEDED_TAB_KEYS.BASIC]: REFERENCE_TAB_KEYS.BASIC,
  [CONFIRM_NEEDED_TAB_KEYS.MFDS]: REFERENCE_TAB_KEYS.LICENSE,
  [CONFIRM_NEEDED_TAB_KEYS.MAPPING]: REFERENCE_TAB_KEYS.MAPPING,
};

interface CreateModalContext {
  copyFlowTab: ReferenceTabKey | null;
  referenceRow: Record<string, unknown> | null;
  settlementRow: SettlementRow;
  isEdit: boolean;
  editInitialDetail: DrugManualDetail | null;
  editDrugCode: string | null;
}

interface ConfirmNeededDrugsModalProps {
  open: boolean;
  baseYm: string;
  onClose: () => void;
  onValidationRefresh?: () => void;
}

const asRecord = (row: object): Record<string, unknown> =>
  row as Record<string, unknown>;

const SelectRadioCell: React.FC<{ value: string }> = ({ value }) => (
  <TableCell className="text-center">
    <RadioGroupItem
      value={value}
      aria-label="선택"
      onClick={(event) => event.stopPropagation()}
    />
  </TableCell>
);

const DelimitedCell: React.FC<{
  value: unknown;
  delimiter: string;
}> = ({ value, delimiter }) => (
  <TableCell className="whitespace-pre-line">
    {formatDelimitedMultiline(value, delimiter)}
  </TableCell>
);

export const ConfirmNeededDrugsModal: React.FC<
  ConfirmNeededDrugsModalProps
> = ({ open, baseYm, onClose, onValidationRefresh }) => {
  const toast = useToast();
  const updateDrug = useUpdateDrugDataDrug();
  const [items, setItems] = useState<CommissionValidationItem[]>([]);
  const [missingCount, setMissingCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchDrugName, setSearchDrugName] = useState('');
  const [searchPharmaCompanyName, setSearchPharmaCompanyName] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResult, setSearchResult] =
    useState<DrugCandidateSearchResult | null>(null);
  const [activeTab, setActiveTab] = useState<ConfirmNeededTabKey>(
    CONFIRM_NEEDED_TAB_KEYS.BASIC,
  );
  const [settlementSelectedKey, setSettlementSelectedKey] = useState<
    string | null
  >(null);
  const [basicSelectedKey, setBasicSelectedKey] = useState<string | null>(null);
  const [mfdsSelectedKey, setMfdsSelectedKey] = useState<string | null>(null);
  const [mappingSelectedKey, setMappingSelectedKey] = useState<string | null>(
    null,
  );
  const [copyBasicLoading, setCopyBasicLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModalContext, setCreateModalContext] =
    useState<CreateModalContext | null>(null);
  const [settlementEditLoading, setSettlementEditLoading] = useState(false);
  const [createdDrugDetailOpen, setCreatedDrugDetailOpen] = useState(false);
  const [createdDrugDetailLoading, setCreatedDrugDetailLoading] =
    useState(false);
  const [createdDrugDetail, setCreatedDrugDetail] =
    useState<DrugManualDetail | null>(null);
  const [createdDrugDetailMeta, setCreatedDrugDetailMeta] = useState({
    drugName: '',
    drugCodePharma: '',
  });
  const searchRequestInFlightRef = useRef(false);

  const resetTransientState = useCallback(() => {
    setSearchDrugName('');
    setSearchPharmaCompanyName('');
    setSearchLoading(false);
    setSearchResult(null);
    setActiveTab(CONFIRM_NEEDED_TAB_KEYS.BASIC);
    setSettlementSelectedKey(null);
    setBasicSelectedKey(null);
    setMfdsSelectedKey(null);
    setMappingSelectedKey(null);
    setCopyBasicLoading(false);
    setCreateModalOpen(false);
    setCreateModalContext(null);
    setSettlementEditLoading(false);
    setCreatedDrugDetailOpen(false);
    setCreatedDrugDetailLoading(false);
    setCreatedDrugDetail(null);
    setCreatedDrugDetailMeta({ drugName: '', drugCodePharma: '' });
  }, []);

  const loadValidation = useCallback(async () => {
    if (!baseYm) return;
    setLoading(true);
    try {
      const result = await drugDataService.validateCommissionDrugCodes(baseYm);
      setItems(result.items ?? []);
      setMissingCount(result.totalMissingCount ?? result.items?.length ?? 0);
    } catch (e) {
      handleApiError(e, toast);
    } finally {
      setLoading(false);
    }
  }, [baseYm, toast]);

  useEffect(() => {
    if (!open) {
      resetTransientState();
      return;
    }
    if (!baseYm) return;
    resetTransientState();
    void loadValidation();
  }, [open, baseYm, loadValidation, resetTransientState]);

  const validationRows = useMemo(
    () =>
      items.map((row, index) => ({
        ...row,
        key: validationRowKey(row, index),
      })),
    [items],
  );

  const basicRows = useMemo(() => {
    const rows = searchResult?.drugBasicInfoCandidates ?? [];
    return rows.map((row, index) => ({
      ...row,
      key: `basic-${row.drugCode ?? index}-${index}`,
    }));
  }, [searchResult]);

  const mfdsRows = useMemo(() => {
    const rows = searchResult?.drugInfoMfdsCandidates ?? [];
    return rows.map((row, index) => ({
      ...row,
      key: `mfds-${row.itemSeq ?? row.barCode ?? index}-${index}`,
    }));
  }, [searchResult]);

  const mappingRows = useMemo(() => {
    const rows = searchResult?.drugCodeMappingCandidates ?? [];
    return rows.map((row, index) => ({
      ...row,
      key: `mapping-${row.drugCodeHira ?? row.barCode ?? index}-${index}`,
    }));
  }, [searchResult]);

  const selectedSettlementRow = useMemo(
    () =>
      validationRows.find((row) => row.key === settlementSelectedKey) ?? null,
    [validationRows, settlementSelectedKey],
  );

  const selectedBasicRow = useMemo(
    () => basicRows.find((row) => row.key === basicSelectedKey) ?? null,
    [basicRows, basicSelectedKey],
  );

  const selectedMfdsRow = useMemo(
    () => mfdsRows.find((row) => row.key === mfdsSelectedKey) ?? null,
    [mfdsRows, mfdsSelectedKey],
  );

  const selectedMappingRow = useMemo(
    () => mappingRows.find((row) => row.key === mappingSelectedKey) ?? null,
    [mappingRows, mappingSelectedKey],
  );

  const selectedReferenceRow = useMemo(() => {
    if (activeTab === CONFIRM_NEEDED_TAB_KEYS.BASIC) return selectedBasicRow;
    if (activeTab === CONFIRM_NEEDED_TAB_KEYS.MFDS) return selectedMfdsRow;
    return selectedMappingRow;
  }, [activeTab, selectedBasicRow, selectedMfdsRow, selectedMappingRow]);

  const fetchCreatedDrugDetail = useCallback(
    async (record: CommissionValidationItem) => {
      const settlementRow = validationItemToSettlementRow(record);
      const drugCodePharma = settlementRow.pharmaDrugCode?.trim();
      if (!baseYm) throw new Error('기준월 정보가 없습니다.');
      if (!drugCodePharma) {
        throw new Error('제약사 약품코드 정보가 없습니다.');
      }
      return drugDataService.findLatestDrugByPharmaCode(baseYm, drugCodePharma);
    },
    [baseYm],
  );

  const openCreateModal = (
    copyFlowTab: ReferenceTabKey | null,
    referenceRow: Record<string, unknown> | null = null,
  ) => {
    if (!selectedSettlementRow) {
      toast.warning('정산수수료 정보 의약품을 선택해주세요.');
      return;
    }
    setCreateModalContext({
      copyFlowTab,
      referenceRow,
      settlementRow: validationItemToSettlementRow(selectedSettlementRow),
      isEdit: false,
      editInitialDetail: null,
      editDrugCode: null,
    });
    setCreateModalOpen(true);
  };

  const handleCreateFromSettlement = async () => {
    if (!selectedSettlementRow) {
      toast.warning('정산수수료 정보 의약품을 선택해주세요.');
      return;
    }

    if (!isSettlementRowCreated(selectedSettlementRow)) {
      openCreateModal(null, null);
      return;
    }

    setSettlementEditLoading(true);
    try {
      const detail = await fetchCreatedDrugDetail(selectedSettlementRow);
      const settlementRow = validationItemToSettlementRow(
        selectedSettlementRow,
      );
      setCreateModalContext({
        copyFlowTab: null,
        referenceRow: null,
        settlementRow,
        isEdit: true,
        editInitialDetail: detail,
        editDrugCode: resolveEditDrugCodeFromDetail(detail, settlementRow),
      });
      setCreateModalOpen(true);
    } catch (err) {
      handleApiError(err, toast);
    } finally {
      setSettlementEditLoading(false);
    }
  };

  const handleOpenCreatedDrugDetail = async (
    record: CommissionValidationItem,
  ) => {
    const settlementRow = validationItemToSettlementRow(record);
    setCreatedDrugDetailMeta({
      drugName: record.drugName ?? '',
      drugCodePharma: settlementRow.pharmaDrugCode ?? '',
    });
    setCreatedDrugDetail(null);
    setCreatedDrugDetailOpen(true);
    setCreatedDrugDetailLoading(true);
    try {
      const data = await fetchCreatedDrugDetail(record);
      setCreatedDrugDetail(data);
    } catch (err) {
      setCreatedDrugDetailOpen(false);
      handleApiError(err, toast);
    } finally {
      setCreatedDrugDetailLoading(false);
    }
  };

  const handleCreateFromReferenceTab = () => {
    if (!selectedReferenceRow) {
      toast.warning('하단 목록에서 의약품을 선택해주세요.');
      return;
    }
    openCreateModal(
      TAB_REFERENCE_KEY[activeTab],
      asRecord(selectedReferenceRow),
    );
  };

  const handleCreateModalSuccess = async () => {
    setCreateModalOpen(false);
    setCreateModalContext(null);
    await loadValidation();
    onValidationRefresh?.();
  };

  const handleCopyBasicDrugCode = async () => {
    if (!selectedSettlementRow || !selectedBasicRow) {
      toast.warning('정산수수료 의약품과 기초정보 의약품을 각각 선택해주세요.');
      return;
    }

    const { refYm, drugCode, body } = buildBasicCopyPatchRequest({
      refYm: baseYm,
      settlementRow: validationItemToSettlementRow(selectedSettlementRow),
      basicRow: selectedBasicRow,
    });

    if (!refYm || !drugCode) {
      toast.warning('기준월 또는 의약품 코드 정보가 없습니다.');
      return;
    }
    if (!body.drugCodePharma) {
      toast.warning('정산수수료 의약품에 제약사 약품코드가 없습니다.');
      return;
    }

    setCopyBasicLoading(true);
    try {
      await updateDrug.mutateAsync({ refYm, drugCode, body });
      toast.success('제약사 약품코드가 반영되었습니다.');
      await loadValidation();
      onValidationRefresh?.();
      setSettlementSelectedKey(null);
      setBasicSelectedKey(null);
    } catch (err) {
      handleApiError(err, toast);
    } finally {
      setCopyBasicLoading(false);
    }
  };

  const runSearchCandidates = useCallback(
    async (overrides?: { drugName?: string; pharmaCompanyName?: string }) => {
      if (!baseYm || searchRequestInFlightRef.current) return;
      const drugName = (overrides?.drugName ?? searchDrugName).trim();
      const pharmaCompanyName = (
        overrides?.pharmaCompanyName ?? searchPharmaCompanyName
      ).trim();
      if (!drugName) {
        toast.warning('의약품명을 입력해주세요.');
        return;
      }
      searchRequestInFlightRef.current = true;
      setSearchLoading(true);
      try {
        const result = await drugDataService.searchDrugCandidates({
          baseYm,
          drugName,
          ...(pharmaCompanyName ? { pharmaCompanyName } : {}),
        });
        setSearchResult(result);
        setBasicSelectedKey(null);
        setMfdsSelectedKey(null);
        setMappingSelectedKey(null);
        setActiveTab(CONFIRM_NEEDED_TAB_KEYS.BASIC);
        if (countDrugCandidateResults(result) === 0) {
          toast.info('조회된 결과가 없습니다.');
        }
      } catch (err) {
        setSearchResult(null);
        handleApiError(err, toast);
      } finally {
        searchRequestInFlightRef.current = false;
        setSearchLoading(false);
      }
    },
    [baseYm, searchDrugName, searchPharmaCompanyName, toast],
  );

  const handleSearchCandidates = async (event: React.FormEvent) => {
    event.preventDefault();
    await runSearchCandidates();
  };

  const handleSettlementSelect = (key: string) => {
    setSettlementSelectedKey(key);
    const row = validationRows.find((item) => item.key === key);
    if (!row) return;
    const keyword = buildSettlementDrugSearchKeyword(row.drugName);
    if (!keyword) return;
    setSearchDrugName(keyword);
    void runSearchCandidates({ drugName: keyword });
  };

  const handleRowActivate = (
    event: React.MouseEvent,
    key: string,
    onSelect: (nextKey: string) => void,
  ) => {
    const target = event.target;
    if (
      target instanceof Element &&
      (target.closest('button') || target.closest('[role="radio"]'))
    ) {
      return;
    }
    onSelect(key);
  };

  const canCreateFromSettlement = Boolean(settlementSelectedKey);
  const hasSettlementAndBasicSelection = Boolean(
    settlementSelectedKey && basicSelectedKey,
  );
  const hasSettlementAndMfdsSelection = Boolean(
    settlementSelectedKey && mfdsSelectedKey,
  );
  const hasSettlementAndMappingSelection = Boolean(
    settlementSelectedKey && mappingSelectedKey,
  );
  const canCopyBasicDrugCode =
    activeTab === CONFIRM_NEEDED_TAB_KEYS.BASIC &&
    hasSettlementAndBasicSelection;
  const canCreateFromReferenceTab =
    activeTab === CONFIRM_NEEDED_TAB_KEYS.MFDS
      ? hasSettlementAndMfdsSelection
      : activeTab === CONFIRM_NEEDED_TAB_KEYS.MAPPING
        ? hasSettlementAndMappingSelection
        : false;

  const candidateEmptyText = searchResult
    ? activeTab === CONFIRM_NEEDED_TAB_KEYS.BASIC
      ? '조회된 의약품기초정보 후보가 없습니다.'
      : activeTab === CONFIRM_NEEDED_TAB_KEYS.MFDS
        ? '조회된 제품허가상세정보 후보가 없습니다.'
        : '조회된 의약품코드매핑 후보가 없습니다.'
    : '검색 후 결과가 표시됩니다.';

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent
          className="flex h-[90vh] max-h-[90vh] w-[min(96vw,1280px)] max-w-[min(96vw,1280px)] flex-col gap-4 overflow-hidden px-6 py-6"
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
        >
          <DialogHeader className="shrink-0 items-start space-y-1 text-left">
            <DialogTitle>확인 필요 의약품</DialogTitle>
            {baseYm ? (
              <p className="text-sm text-muted-foreground">
                기준월 {baseYm} · 확인 필요 {missingCount}건
              </p>
            ) : null}
          </DialogHeader>

          {loading ? (
            <LoadingSpinner tip="수수료 검증 결과를 불러오는 중..." />
          ) : (
            <div className="flex min-h-0 flex-1 flex-col gap-3">
              <section className="flex min-h-0 flex-1 flex-col">
                <h3 className="mb-3 shrink-0 text-sm font-semibold">
                  정산수수료 정보 의약품
                </h3>
                <div className="min-h-0 flex-1 overflow-auto rounded-12 border border-border">
                  <RadioGroup
                    className="gap-0"
                    value={settlementSelectedKey ?? ''}
                    onValueChange={handleSettlementSelect}
                  >
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>순번</TableHead>
                          <TableHead>제약사 명</TableHead>
                          <TableHead>의약품 명</TableHead>
                          <TableHead>제약사 약품코드</TableHead>
                          <TableHead>약가</TableHead>
                          <TableHead>매핑 후보 수</TableHead>
                          <TableHead>MFDS 후보 수</TableHead>
                          <TableHead>생성 여부</TableHead>
                          <TableHead className="w-20 text-center">
                            선택
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {validationRows.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={9}
                              className="text-center text-muted-foreground"
                            >
                              검증 결과 항목이 없습니다.
                            </TableCell>
                          </TableRow>
                        ) : (
                          validationRows.map((row, index) => (
                            <TableRow
                              key={row.key}
                              data-state={
                                settlementSelectedKey === row.key
                                  ? 'selected'
                                  : undefined
                              }
                              className="cursor-pointer"
                              onClick={(event) =>
                                handleRowActivate(
                                  event,
                                  row.key,
                                  handleSettlementSelect,
                                )
                              }
                            >
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>
                                {resolvePharmaCompanyName(row)}
                              </TableCell>
                              <TableCell className="max-w-[220px] truncate">
                                {displayCell(row.drugName)}
                              </TableCell>
                              <TableCell>
                                {resolveValidationPharmaDrugCode(row)}
                              </TableCell>
                              <TableCell>
                                {resolveValidationPrice(row)}
                              </TableCell>
                              <TableCell>
                                {candidateListCount(row.mappingCandidates)}
                              </TableCell>
                              <TableCell>
                                {candidateListCount(row.mfdsCandidates)}
                              </TableCell>
                              <TableCell>
                                {isSettlementRowCreated(row) ? (
                                  <div className="flex items-center gap-1">
                                    <Badge variant="default">생성됨</Badge>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      aria-label="생성 의약품 정보 조회"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        void handleOpenCreatedDrugDetail(row);
                                      }}
                                    >
                                      <ExternalLink />
                                    </Button>
                                  </div>
                                ) : (
                                  <Badge variant="secondary">생성 안 됨</Badge>
                                )}
                              </TableCell>
                              <SelectRadioCell value={row.key} />
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </RadioGroup>
                </div>
                <FooterRow className="mt-3 shrink-0">
                  <Button
                    type="button"
                    disabled={!canCreateFromSettlement || settlementEditLoading}
                    loading={settlementEditLoading}
                    onClick={() => void handleCreateFromSettlement()}
                  >
                    {isSettlementRowCreated(selectedSettlementRow)
                      ? '의약품 정보 수정'
                      : '선택 정보 의약품 생성'}
                  </Button>
                </FooterRow>
              </section>

              <div className="shrink-0 border-t border-border" />

              <section className="flex min-h-0 flex-1 flex-col">
                <ToolbarRow
                  justify="between"
                  className="mb-3 shrink-0 flex-nowrap gap-3"
                >
                  <div
                    role="tablist"
                    aria-label="후보 출처"
                    className="flex min-w-0 gap-1 border-b border-border/60"
                  >
                    {(
                      [
                        {
                          key: CONFIRM_NEEDED_TAB_KEYS.BASIC,
                          label: `의약품기초정보(${basicRows.length})`,
                        },
                        {
                          key: CONFIRM_NEEDED_TAB_KEYS.MFDS,
                          label: `제품허가상세정보(${mfdsRows.length})`,
                        },
                        {
                          key: CONFIRM_NEEDED_TAB_KEYS.MAPPING,
                          label: `의약품코드매핑정보(${mappingRows.length})`,
                        },
                      ] as const
                    ).map((tab) => (
                      <Button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === tab.key}
                        variant={activeTab === tab.key ? 'default' : 'ghost'}
                        size="sm"
                        className="rounded-b-none"
                        onClick={() => setActiveTab(tab.key)}
                      >
                        {tab.label}
                      </Button>
                    ))}
                  </div>
                  <Row
                    as="form"
                    wrap={false}
                    align="center"
                    gap={12}
                    className="mb-0 shrink-0"
                    onSubmit={(event) => void handleSearchCandidates(event)}
                  >
                    <div className="w-44 shrink-0">
                      <Input
                        value={searchDrugName}
                        onChange={(e) => setSearchDrugName(e.target.value)}
                        placeholder="의약품 명 검색"
                        aria-label="의약품 명 검색"
                      />
                    </div>
                    <div className="w-48 shrink-0">
                      <Input
                        value={searchPharmaCompanyName}
                        onChange={(e) =>
                          setSearchPharmaCompanyName(e.target.value)
                        }
                        placeholder="제약사 명 검색 (선택)"
                        aria-label="제약사 명 검색"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="ruda-button shrink-0"
                      disabled={searchLoading}
                      loading={searchLoading}
                    >
                      <Search className="h-4 w-4" />
                      검색
                    </Button>
                  </Row>
                </ToolbarRow>

                <div className="min-h-0 flex-1 overflow-auto rounded-12 border border-border">
                  {searchLoading ? (
                    <LoadingSpinner tip="후보를 검색하는 중..." />
                  ) : activeTab === CONFIRM_NEEDED_TAB_KEYS.BASIC ? (
                    <CandidateBasicTable
                      rows={basicRows}
                      selectedKey={basicSelectedKey}
                      emptyText={candidateEmptyText}
                      onSelect={setBasicSelectedKey}
                      onRowActivate={handleRowActivate}
                    />
                  ) : activeTab === CONFIRM_NEEDED_TAB_KEYS.MFDS ? (
                    <CandidateMfdsTable
                      rows={mfdsRows}
                      selectedKey={mfdsSelectedKey}
                      emptyText={candidateEmptyText}
                      onSelect={setMfdsSelectedKey}
                      onRowActivate={handleRowActivate}
                    />
                  ) : (
                    <CandidateMappingTable
                      rows={mappingRows}
                      selectedKey={mappingSelectedKey}
                      emptyText={candidateEmptyText}
                      onSelect={setMappingSelectedKey}
                      onRowActivate={handleRowActivate}
                    />
                  )}
                </div>

                <FooterRow className="mt-3 shrink-0">
                  {activeTab === CONFIRM_NEEDED_TAB_KEYS.BASIC ? (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={!canCopyBasicDrugCode || copyBasicLoading}
                      loading={copyBasicLoading}
                      onClick={() => void handleCopyBasicDrugCode()}
                    >
                      선택 의약품 코드 복사
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      disabled={!canCreateFromReferenceTab}
                      onClick={handleCreateFromReferenceTab}
                    >
                      선택 의약품 정보 생성
                    </Button>
                  )}
                </FooterRow>
              </section>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {createModalContext ? (
        <DrugInfoCreateFromSettlementModal
          open={createModalOpen}
          onCancel={() => {
            setCreateModalOpen(false);
            setCreateModalContext(null);
          }}
          onSuccess={() => void handleCreateModalSuccess()}
          refYm={baseYm}
          settlementRow={createModalContext.settlementRow}
          referenceRow={createModalContext.referenceRow}
          copyFlowTab={createModalContext.copyFlowTab}
          isEdit={createModalContext.isEdit}
          editInitialDetail={createModalContext.editInitialDetail}
          editDrugCode={createModalContext.editDrugCode}
        />
      ) : null}

      <CreatedDrugDetailModal
        open={createdDrugDetailOpen}
        loading={createdDrugDetailLoading}
        detail={createdDrugDetail}
        drugName={createdDrugDetailMeta.drugName}
        drugCodePharma={createdDrugDetailMeta.drugCodePharma}
        onClose={() => setCreatedDrugDetailOpen(false)}
      />
    </>
  );
};

interface CandidateTableProps<T extends { key: string }> {
  rows: T[];
  selectedKey: string | null;
  emptyText: string;
  onSelect: (key: string) => void;
  onRowActivate: (
    event: React.MouseEvent,
    key: string,
    onSelect: (nextKey: string) => void,
  ) => void;
}

const CandidateBasicTable: React.FC<
  CandidateTableProps<DrugBasicInfoCandidate & { key: string }>
> = ({ rows, selectedKey, emptyText, onSelect, onRowActivate }) => (
  <RadioGroup
    className="gap-0"
    value={selectedKey ?? ''}
    onValueChange={onSelect}
  >
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>제약사 명</TableHead>
          <TableHead>의약품 명</TableHead>
          <TableHead>의약품 코드</TableHead>
          <TableHead>약가</TableHead>
          <TableHead>급여상태</TableHead>
          <TableHead>정보구분</TableHead>
          <TableHead>제약사 약품코드</TableHead>
          <TableHead className="w-20 text-center">선택</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell
              colSpan={8}
              className="text-center text-muted-foreground"
            >
              {emptyText}
            </TableCell>
          </TableRow>
        ) : (
          rows.map((row) => (
            <TableRow
              key={row.key}
              data-state={selectedKey === row.key ? 'selected' : undefined}
              className="cursor-pointer"
              onClick={(event) => onRowActivate(event, row.key, onSelect)}
            >
              <TableCell>{displayCell(row.pharmaCompanyName)}</TableCell>
              <TableCell className="max-w-[240px] truncate">
                {displayCell(row.drugName)}
              </TableCell>
              <TableCell>{displayCell(row.drugCode)}</TableCell>
              <TableCell>{displayCell(row.maxPrice)}</TableCell>
              <TableCell>{displayCell(row.reimbursementType)}</TableCell>
              <TableCell>{displayCell(row.dataSourceType)}</TableCell>
              <TableCell>{displayCell(row.drugCodePharma)}</TableCell>
              <SelectRadioCell value={row.key} />
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  </RadioGroup>
);

const CandidateMfdsTable: React.FC<
  CandidateTableProps<DrugMfdsCandidate & { key: string }>
> = ({ rows, selectedKey, emptyText, onSelect, onRowActivate }) => (
  <RadioGroup
    className="gap-0"
    value={selectedKey ?? ''}
    onValueChange={onSelect}
  >
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>제약사 명</TableHead>
          <TableHead>의약품 명</TableHead>
          <TableHead>포장단위</TableHead>
          <TableHead>주성분</TableHead>
          <TableHead>바코드</TableHead>
          <TableHead>ATC 코드</TableHead>
          <TableHead className="w-20 text-center">선택</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell
              colSpan={7}
              className="text-center text-muted-foreground"
            >
              {emptyText}
            </TableCell>
          </TableRow>
        ) : (
          rows.map((row) => (
            <TableRow
              key={row.key}
              data-state={selectedKey === row.key ? 'selected' : undefined}
              className="cursor-pointer"
              onClick={(event) => onRowActivate(event, row.key, onSelect)}
            >
              <TableCell>{displayCell(row.entpName)}</TableCell>
              <TableCell className="max-w-[280px] truncate">
                {displayCell(row.itemName)}
              </TableCell>
              <DelimitedCell value={row.packUnit} delimiter="," />
              <DelimitedCell value={row.materialName} delimiter="|" />
              <DelimitedCell value={row.barCode} delimiter="," />
              <DelimitedCell value={row.atcCode} delimiter="," />
              <SelectRadioCell value={row.key} />
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  </RadioGroup>
);

const CandidateMappingTable: React.FC<
  CandidateTableProps<DrugCodeMappingCandidate & { key: string }>
> = ({ rows, selectedKey, emptyText, onSelect, onRowActivate }) => (
  <RadioGroup
    className="gap-0"
    value={selectedKey ?? ''}
    onValueChange={onSelect}
  >
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>제약사 명</TableHead>
          <TableHead>의약품 명</TableHead>
          <TableHead>의약품 코드</TableHead>
          <TableHead>함량</TableHead>
          <TableHead>바코드</TableHead>
          <TableHead>ATC 코드</TableHead>
          <TableHead className="w-20 text-center">선택</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell
              colSpan={7}
              className="text-center text-muted-foreground"
            >
              {emptyText}
            </TableCell>
          </TableRow>
        ) : (
          rows.map((row) => (
            <TableRow
              key={row.key}
              data-state={selectedKey === row.key ? 'selected' : undefined}
              className="cursor-pointer"
              onClick={(event) => onRowActivate(event, row.key, onSelect)}
            >
              <TableCell>{displayCell(row.pharmaCompanyName)}</TableCell>
              <TableCell className="max-w-[280px] truncate">
                {displayCell(row.drugName)}
              </TableCell>
              <TableCell>{displayCell(row.drugCodeHira)}</TableCell>
              <TableCell>{displayCell(row.ingredientAmount)}</TableCell>
              <TableCell>{displayCell(row.barCode)}</TableCell>
              <DelimitedCell value={row.atcCode} delimiter="," />
              <SelectRadioCell value={row.key} />
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  </RadioGroup>
);
